import dayjs from 'dayjs';
import {AiAgentCustomColumnFormat} from 'src/types';

const EMPTY = '—';

const toFiniteNumber = (value: unknown): number | null => {
    if (value == null) return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
};

const formatNumber = (n: number, fractionDigits = 0): string =>
    n.toLocaleString(undefined, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });

const formatCurrency = (n: number, code: string): string =>
    `${code} ${n.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

const formatDuration = (seconds: number): string => {
    const total = Math.max(0, Math.floor(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}h ${m}min`;
    if (m > 0) return `${m}min`;
    return `${s}s`;
};

const formatDate = (value: unknown): string => {
    if (value == null) return EMPTY;
    const d = typeof value === 'number' ? dayjs(value) : dayjs(String(value));
    return d.isValid() ? d.format('DD.MM.YYYY') : EMPTY;
};

export const formatCell = (value: unknown, format: AiAgentCustomColumnFormat): string => {
    switch (format) {
        case 'text': {
            if (value == null || value === '') return EMPTY;
            return String(value);
        }
        case 'number': {
            const n = toFiniteNumber(value);
            return n == null ? EMPTY : formatNumber(n);
        }
        case 'kwh': {
            const n = toFiniteNumber(value);
            return n == null ? EMPTY : `${formatNumber(n, 1)} kWh`;
        }
        case 'currency_chf': {
            const n = toFiniteNumber(value);
            return n == null ? EMPTY : formatCurrency(n, 'CHF');
        }
        case 'currency_eur': {
            const n = toFiniteNumber(value);
            return n == null ? EMPTY : formatCurrency(n, 'EUR');
        }
        case 'currency_uah': {
            const n = toFiniteNumber(value);
            return n == null ? EMPTY : formatCurrency(n, 'UAH');
        }
        case 'percent': {
            const n = toFiniteNumber(value);
            return n == null ? EMPTY : `${formatNumber(n, 1)}%`;
        }
        case 'date':
            return formatDate(value);
        case 'duration': {
            const n = toFiniteNumber(value);
            return n == null ? EMPTY : formatDuration(n);
        }
        default:
            return value == null ? EMPTY : String(value);
    }
};

export const compareCells = (
    a: unknown,
    b: unknown,
    format: AiAgentCustomColumnFormat
): number => {
    const aNil = a == null || a === '';
    const bNil = b == null || b === '';
    if (aNil && bNil) return 0;
    if (aNil) return 1;
    if (bNil) return -1;

    if (format === 'text') {
        return String(a).localeCompare(String(b), undefined, {sensitivity: 'base', numeric: true});
    }
    if (format === 'date') {
        const da = typeof a === 'number' ? dayjs(a) : dayjs(String(a));
        const db = typeof b === 'number' ? dayjs(b) : dayjs(String(b));
        return da.valueOf() - db.valueOf();
    }
    const na = toFiniteNumber(a);
    const nb = toFiniteNumber(b);
    if (na == null && nb == null) return 0;
    if (na == null) return 1;
    if (nb == null) return -1;
    return na - nb;
};
